/**
 * Theme color tokens used by AI-generated documents.
 *
 * The model never writes a hex value for smart-block item colors or page tones. It picks one of
 * these tokens instead. The simple-to-builder converter expands a token to a CSS value that keeps
 * referring to the document theme, so changing the theme recolors the block without a re-generate.
 */

export const THEME_COLOR_TOKENS = [
  'primary',
  'primary-light',
  'primary-dark',
  'secondary',
  'secondary-light',
  'secondary-dark',
  'heading',
  'body',
] as const;

export type ThemeColorToken = (typeof THEME_COLOR_TOKENS)[number];

const THEME_COLOR_TOKEN_CSS: Record<ThemeColorToken, string> = {
  primary: 'var(--th-theme-primary-color)',
  'primary-light': 'color-mix(in srgb, var(--th-theme-primary-color) 55%, white)',
  'primary-dark': 'color-mix(in srgb, var(--th-theme-primary-color) 55%, black)',
  secondary: 'var(--th-theme-secondary-color)',
  'secondary-light': 'color-mix(in srgb, var(--th-theme-secondary-color) 55%, white)',
  'secondary-dark': 'color-mix(in srgb, var(--th-theme-secondary-color) 55%, black)',
  heading: 'var(--th-theme-heading-color)',
  body: 'var(--th-theme-body-color)',
};

const CSS_TO_THEME_COLOR_TOKEN = new Map<string, ThemeColorToken>(
  Object.entries(THEME_COLOR_TOKEN_CSS).map(([token, css]) => [css, token as ThemeColorToken]),
);

export const isThemeColorToken = (value: unknown): value is ThemeColorToken =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(THEME_COLOR_TOKEN_CSS, value);

/**
 * Expands a semantic token to the CSS value stored in builder JSON.
 * Any other string (legacy hex, gradient, existing CSS value) is returned unchanged.
 */
export const resolveThemeColorExpression = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  if (isThemeColorToken(value)) return THEME_COLOR_TOKEN_CSS[value];
  return value;
};

/**
 * Reverses a converter-produced CSS value back to its token, so a builder document round-trips
 * to simple JSON as the token the model can reason about. User-picked hex values stay hex.
 */
export const themeColorTokenFromCss = (value?: string | null): ThemeColorToken | undefined => {
  if (!value) return undefined;
  return CSS_TO_THEME_COLOR_TOKEN.get(value);
};
